import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import HistoryIcon from '@mui/icons-material/History';
import RestoreIcon from '@mui/icons-material/Restore';
import UndoIcon from '@mui/icons-material/Undo';
import {
  Box,
  Button,
  Chip,
  Collapse,
  IconButton,
  Snackbar,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useCallback, useState } from 'react';
import type { CreatePromptRequest, PromptRecord, PromptScope } from '../../../shared/types/prompts';
import { useCreatePromptMutation, useListPromptsQuery } from '../../../store/api/rpcSlice';
import { withStaleCaseAcknowledgmentRetry } from '../staleAcknowledgment';

interface EditColumnProps {
  profileSlug: string;
  promptSlug: string;
  promptScope: PromptScope;
  baseText: string;
  candidateText: string;
  onCandidateChange: (text: string) => void;
  onSaved: (prompt: PromptRecord) => void;
  onTestAndCompare: () => void;
  onBack: () => void;
}

export function buildCreatePromptRequest({
  promptSlug,
  scope,
  candidateText,
  changelog,
}: {
  promptSlug: string;
  scope: PromptScope;
  candidateText: string;
  changelog: string;
}): CreatePromptRequest {
  return {
    slug: promptSlug,
    scope,
    text: candidateText,
    changelog,
  };
}

export function saveCandidatePrompt<T>(
  input: {
    promptSlug: string;
    scope: PromptScope;
    candidateText: string;
    changelog: string;
  },
  execute: (request: CreatePromptRequest) => Promise<T>
): Promise<T> {
  return withStaleCaseAcknowledgmentRetry(buildCreatePromptRequest(input), execute);
}

export function EditColumn({
  promptSlug,
  promptScope,
  baseText,
  candidateText,
  onCandidateChange,
  onSaved,
}: EditColumnProps) {
  const isDirty = candidateText !== baseText;
  const [createPrompt, { isLoading: isSaving }] = useCreatePromptMutation();
  const [saveMessage, setSaveMessage] = useState<string | null>(null);
  const [showHistory, setShowHistory] = useState(false);
  const [changelog, setChangelog] = useState('');

  // Fetch version history for this prompt slug
  const { data: versionsData } = useListPromptsQuery({
    slugs: [promptSlug],
    include_deleted: true,
    limit: 20,
  });
  const versions = versionsData?.items ?? [];

  const handleSave = useCallback(async () => {
    if (!changelog.trim()) {
      setSaveMessage('Please add a changelog message');
      return;
    }
    try {
      const savedPrompt = await saveCandidatePrompt(
        { promptSlug, scope: promptScope, candidateText, changelog },
        (request) => createPrompt(request).unwrap()
      );
      onSaved(savedPrompt);
      setSaveMessage('Saved as new version');
      setChangelog('');
    } catch (err) {
      const errMsg =
        (err as { data?: { error?: string } })?.data?.error ??
        (err as { message?: string })?.message ??
        'Unknown error';
      setSaveMessage(`Save failed: ${errMsg}`);
    }
  }, [createPrompt, promptSlug, promptScope, candidateText, changelog, onSaved]);

  const handleReset = useCallback(() => {
    onCandidateChange(baseText);
  }, [baseText, onCandidateChange]);

  const handleCopyCandidate = useCallback(() => {
    navigator.clipboard.writeText(candidateText);
    setSaveMessage('Copied to clipboard');
  }, [candidateText]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header with prompt slug and actions */}
      <Box
        sx={{
          px: 2,
          py: 1,
          borderBottom: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <Typography variant="body2" fontWeight={600} sx={{ flex: 1, fontSize: 13 }}>
          {promptSlug}
        </Typography>
        <Tooltip title="Version History">
          <IconButton size="small" onClick={() => setShowHistory(!showHistory)}>
            <HistoryIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Reset to base">
          <span>
            <IconButton size="small" onClick={handleReset} disabled={!isDirty}>
              <UndoIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Copy candidate">
          <IconButton size="small" onClick={handleCopyCandidate}>
            <ContentCopyIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        {isDirty && (
          <Chip
            label="Modified"
            size="small"
            color="warning"
            sx={{ height: 20, '& .MuiChip-label': { fontSize: 10 } }}
          />
        )}
      </Box>

      {/* Version history panel (collapsible) */}
      <Collapse in={showHistory}>
        <Box
          sx={{
            maxHeight: 200,
            overflow: 'auto',
            borderBottom: '1px solid',
            borderColor: 'divider',
            bgcolor: 'grey.50',
          }}
        >
          <Box sx={{ px: 2, py: 0.5 }}>
            <Typography variant="caption" fontWeight={600} color="text.secondary">
              Version History
            </Typography>
          </Box>
          {versions.length === 0 ? (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ px: 2, pb: 1, display: 'block' }}
            >
              No versions found
            </Typography>
          ) : (
            versions.map((v) => (
              <Box
                key={v.id}
                sx={{
                  px: 2,
                  py: 0.5,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  '&:hover': { bgcolor: 'action.hover' },
                }}
              >
                <Chip
                  label={`v${v.version}`}
                  size="small"
                  variant="outlined"
                  sx={{ height: 18, '& .MuiChip-label': { fontSize: 10 } }}
                />
                <Typography variant="caption" sx={{ flex: 1, fontSize: 11 }}>
                  {v.changelog || 'No changelog'}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>
                  {v.scope?.type}
                </Typography>
                <Tooltip title="Restore this version">
                  <IconButton size="small" onClick={() => onCandidateChange(v.text)}>
                    <RestoreIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </Tooltip>
              </Box>
            ))
          )}
        </Box>
      </Collapse>

      {/* Side-by-side editors */}
      <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        {/* Left: Base (read-only) */}
        <Box
          sx={{
            flex: 1,
            borderRight: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box
            sx={{
              px: 1.5,
              py: 0.5,
              borderBottom: '1px solid',
              borderColor: 'divider',
              bgcolor: 'grey.50',
            }}
          >
            <Typography variant="caption" fontWeight={600} color="text.secondary" fontSize={10}>
              BASE (Published)
            </Typography>
          </Box>
          <Box
            sx={{
              flex: 1,
              overflow: 'auto',
              p: 1.5,
              fontFamily: 'monospace',
              fontSize: 12,
              whiteSpace: 'pre-wrap',
              lineHeight: 1.6,
              color: 'text.primary',
            }}
          >
            {baseText}
          </Box>
        </Box>

        {/* Right: Candidate (editable) */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          <Box
            sx={{
              px: 1.5,
              py: 0.5,
              borderBottom: '1px solid',
              borderColor: 'divider',
              bgcolor: isDirty ? 'warning.light' : 'grey.50',
            }}
          >
            <Typography
              variant="caption"
              fontWeight={600}
              color={isDirty ? 'warning.contrastText' : 'text.secondary'}
              fontSize={10}
            >
              CANDIDATE {isDirty ? '(Modified)' : ''}
            </Typography>
          </Box>
          <textarea
            value={candidateText}
            onChange={(e) => onCandidateChange(e.target.value)}
            style={{
              flex: 1,
              width: '100%',
              border: 'none',
              outline: 'none',
              resize: 'none',
              padding: '12px',
              fontFamily: 'monospace',
              fontSize: '12px',
              lineHeight: '1.6',
              backgroundColor: 'transparent',
              boxSizing: 'border-box',
            }}
          />
        </Box>
      </Box>

      {/* Bottom: Save bar */}
      <Box sx={{ borderTop: '1px solid', borderColor: 'divider' }}>
        <Collapse in={isDirty}>
          <Box sx={{ px: 2, py: 1, display: 'flex', gap: 1, alignItems: 'center' }}>
            <TextField
              size="small"
              placeholder="What changed? (required to save)"
              value={changelog}
              onChange={(e) => setChangelog(e.target.value)}
              sx={{ flex: 1, '& .MuiInputBase-root': { fontSize: 12 } }}
            />
            <Button
              variant="contained"
              size="small"
              disabled={isSaving || !changelog.trim()}
              onClick={handleSave}
              sx={{ fontSize: 12, textTransform: 'none' }}
            >
              {isSaving ? 'Saving...' : 'Save Version'}
            </Button>
          </Box>
        </Collapse>
      </Box>

      <Snackbar
        open={!!saveMessage}
        autoHideDuration={3000}
        onClose={() => setSaveMessage(null)}
        message={saveMessage}
      />
    </Box>
  );
}
